/* Manual photo navigation. Read-only catalog requests; the shared basket engine owns cart writes. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('rudca-photo-scroll')) return;
  let addedDescriptionId = 0;
  class RudcaPhotoScroll extends HTMLElement {
    connectedCallback() {
      if (this.connection || this.dataset.photoTheme !== '188331491554' || (!this.hasAttribute('data-photo-global') && !/^\d+$/.test(this.dataset.photoCollection || ''))) return;
      this.connection = new AbortController();
      const signal = this.connection.signal;
      // bfcache restores this same element without another connectedCallback.
      // Cancel old intents and reads on departure; a return gets a fresh lifecycle.
      window.addEventListener('pagehide', () => this.stop(), {signal});
      window.addEventListener('pageshow', (event) => { if (event.persisted && this.isConnected) this.waitForViewport(); }, {signal});
      this.waitForViewport();
    }
    waitForViewport() {
      if (!this.isConnected || this.connection?.signal.aborted || this.controller || this.visibility) return;
      if (!this.hasAttribute('data-photo-lazy')) { this.start(); return; }
      this.visibility = new AbortController();
      const signal = this.visibility.signal;
      this.dataset.photoLoadState = 'deferred';
      this.removeAttribute('aria-busy');
      this.photoWarmup = false;
      this.photoDemand = new Promise((resolve) => { this.resolvePhotoDemand = resolve; });
      const start = (warmup = false) => {
        if (signal.aborted || !this.isConnected) return;
        this.cancelStartTask();
        if (!warmup) {
          this.photoWarmup = false;
          this.resolvePhotoDemand?.(); this.resolvePhotoDemand = null;
          this.visibilityObserver?.disconnect();
          this.visibility?.abort(); this.visibility = null;
          // Promote the same startup; never restart a catalog or control read.
          this.pages?.position();
        } else {
          if (this.controller) return;
          this.photoWarmup = true;
        }
        this.start();
      };
      const canWarm = () => {
        const connection = navigator.connection;
        return document.visibilityState !== 'hidden' && navigator.onLine !== false && !connection?.saveData &&
          !['slow-2g','2g'].includes(connection?.effectiveType);
      };
      const warm = () => {
        if (signal.aborted || this.controller || this.startTask || document.readyState !== 'complete' || !canWarm()) return;
        const task = this.startTask = {idle:Boolean(window.requestIdleCallback)};
        const run = () => {
          if (this.startTask !== task) return;
          this.startTask = null;
          // A hidden tab never spends its idle timeout on speculative reads.
          if (canWarm()) start(true);
        };
        task.id = task.idle ? window.requestIdleCallback(run,{timeout:1000}) : setTimeout(run,200);
      };
      if (window.IntersectionObserver) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) start();
        }, {rootMargin:'1200px 0px'});
        this.visibilityObserver.observe(this);
      } else {
        const check = () => {
          const box = this.getBoundingClientRect();
          if (box.bottom >= -1200 && box.top <= window.innerHeight + 1200) start();
        };
        window.addEventListener('scroll', check, {passive:true,signal});
        window.addEventListener('resize', check, {passive:true,signal});
        check();
      }
      // Keep above-the-fold loading first; warm just the initial live-stock page.
      window.addEventListener('load', warm, {once:true,signal});
      document.addEventListener('visibilitychange', warm, {signal});
      warm();
    }
    cancelStartTask() {
      if (!this.startTask) return;
      if (this.startTask.idle) window.cancelIdleCallback?.(this.startTask.id);
      else clearTimeout(this.startTask.id);
      this.startTask = null;
    }
    async start() {
      if (this.controller || !this.isConnected) return;
      this.rail = this.querySelector('[data-photo-rail]');
      if (!this.rail) return;
      this.controller = new AbortController();
      if (this.hasAttribute('data-photo-global')) {
        // Keep any inert bfcache photos visible until the fresh page replaces them atomically.
        this.links = [];
        this.controlCards = [];
      }
      const retry = this.querySelector('[data-photo-retry]');
      if (retry) retry.hidden = true;
      this.setAttribute('aria-busy', 'true');
      if (!this.querySelector('[data-photo-link]')) this.setAttribute('data-photo-empty-shell', '');
      const signal = this.controller.signal;
      if (this.hasAttribute('data-photo-global')) {
        try {
          this.pages = new window.RudcaPhotoPages(this);
          if (!await this.pages.start(signal)) return;
        } catch (error) {
          if (error.name === 'AbortError' || signal.aborted) return;
          this.removeAttribute('aria-busy');
          const loading = this.querySelector('[data-photo-loading-status]');
          if (loading) loading.hidden = true;
          if (this.pages) this.pages.error(error);
          else {
            this.dataset.photoLoadState = 'error';
            this.dataset.photoErrorCode = 'SCRIPT_INIT';
            if (loading) { loading.textContent = this.dataset.photoError || ''; loading.hidden = false; }
            const code = this.querySelector('[data-photo-error-code]');
            if (code) { code.textContent = 'SCRIPT_INIT'; code.hidden = false; }
          }
          const retry = this.querySelector('[data-photo-retry]');
          if (retry) {
            retry.hidden = false;
            retry.addEventListener('click', () => {
              this.stop();
              retry.hidden = true; this.start();
            }, {once:true,signal});
          }
          return;
        }
      }
      if (signal.aborted || !this.isConnected) return;
      this.removeAttribute('aria-busy');
      const loading = this.querySelector('[data-photo-loading-status]');
      if (loading) loading.hidden = this.querySelectorAll('[data-photo-link]').length > 0;
      this.links = [...this.querySelectorAll('[data-photo-link]')];
      if (!this.links.length) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileAdd = matchMedia('(max-width: 999px)');
      this.mobileAdd.addEventListener('change', () => { this.controlCards.forEach(card => this.mobileAddIcons(card)); this.lockedCards = null; this.lock(); this.scheduleSettle(); }, {signal});
      this.current = Math.min(2, this.links.length - 1);
      this.suppressUntil = 0;
      this.controls = this.querySelector('[data-photo-controls]');
      this.controlCards = [...this.querySelectorAll('[data-photo-control]')];
      this.controlCards.forEach(card => this.mobileAddIcons(card));
      this.epoch = 0;
      this.pendingAdds = new Map();
      this.lock();
      this.measure();
      // A static initial composition centered on the third photo; all photos share a horizontal centre.
      // This is immediate initial layout, never an animated or timed advance.
      this.go(this.current, false);
      this.paint();
      this.rail.addEventListener('scroll', () => {
        // Snap/focus rounding does not invalidate the exact stationary press.
        if (this.pointer?.controlPress && !this.pointer.moved && (!this.locked || this.intent?.cold || this.intent?.settling) && this.validIntent(this.intent)) { this.schedulePaint(); return; }
        this.trackSettled = false; this.releasePointerScrollFocus(); this.hideStockHints(); this.cancelShiftedPendingAdds(); this.lock(); this.schedulePaint(); this.scheduleSettle(); this.scheduleTrackCalm(); }, { passive: true, signal });
      this.rail.addEventListener('scrollend', () => this.finishTrackMotion(), { passive: true, signal });
      this.rail.addEventListener('touchstart', () => { this.touchActive = true; this.reserveActive = true; this.trackSettled = false; }, {passive:true,signal});
      const touchDone = event => { this.touchActive = Boolean(event.touches?.length); if (event.type === 'touchcancel') { this.intent = null; this.releaseClick(); } this.scheduleTrackCalm(); };
      window.addEventListener('touchend',touchDone,{passive:true,signal});
      window.addEventListener('touchcancel',touchDone,{passive:true,signal});
      this.rail.addEventListener('wheel', (event) => {
        const delta = event.shiftKey ? event.deltaY : Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : 0;
        if (delta) {
          const max = Math.max(0,this.rail.scrollWidth - this.rail.clientWidth);
          // Some browsers ignore CSS containment for history-swipe gestures.
          // Keep interior/vertical scrolling native; cancel only outward at an edge.
          if (event.cancelable && !event.ctrlKey && (delta < 0 ? this.rail.scrollLeft <= 1.5 : this.rail.scrollLeft >= max - 1.5)) event.preventDefault();
          this.reserveActive = true; this.trackSettled = false;
          this.scheduleTrackCalm();
          this.pointerScrollFocus = this.rail.contains(document.activeElement) ? document.activeElement : null;
          this.releasePointerScrollFocus();
          this.boundaryIntent(Math.sign(delta));
        }
      }, { passive:false, signal });
      // Capture before the document-level basket engine. Inert also blocks real
      // pointer/keyboard input; this guard rejects delayed and synthetic events.
      const guard = (event) => {
        if (!this.controls?.contains(event.target)) return;
        const card = event.target.closest('[data-photo-control]');
        if (!card) return;
        if (event.type === 'click' && event.detail > 0 && this.pointer?.released && this.intent?.card === card) this.releaseClick();
        const exact = this.cardVisible(card) && card?.closest('[data-photo-item]')?.dataset.photoItem === card?.dataset.photoControl;
        const stale = event.type === 'click' && event.detail > 0 && (!this.validIntent(this.intent) || this.intent.consumed || this.intent.card !== card ||
          (this.intent.button && this.intent.button !== event.target.closest('button')));
        if (this.locked || this.pointer || card.inert || Date.now() < this.suppressUntil || !exact || stale) { event.preventDefault(); event.stopImmediatePropagation(); }
        else if (event.type === 'click' && event.detail > 0) this.intent.consumed = true;
      };
      window.addEventListener('click', guard, { capture: true, signal });
      window.addEventListener('submit', guard, { capture: true, signal });
      window.addEventListener('pointerdown', () => { if (this.pointer?.released) { this.intent = null; this.releaseClick(); } }, {capture:true,signal});
      this.controls?.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.releaseClick();
        const card = event.target.closest('[data-photo-control]');
        const link = card?.closest('[data-photo-item]')?.querySelector('[data-photo-link]');
        const button = event.target.closest('button');
        const settling = this.mobileAdd.matches && event.pointerType === 'touch' && !this.coldControl(card) &&
          (this.locked || this.pendingAdds.has(card?.dataset.photoControl)) &&
          button?.matches('[data-rudca-grid-add], [data-rudca-basket-change="1"]') && !button.disabled && !button.closest('[hidden]');
        this.intent = { epoch: this.epoch, card, link, left:this.rail.scrollLeft, image:this.pendingImage(link),
          fingerprint:this.pendingFingerprint(card), button, settling, cold:this.coldControl(card) && Boolean(event.target.closest('[data-photo-placeholder-add], [data-rudca-grid-add]')) };
        if (event.target.closest('[data-rudca-basket-change="1"]:disabled')) this.revealStock(event.target);
      }, { capture: true, signal });
      if (this.controls) {
        this.controlObserver = new MutationObserver(records => {
          // Decorate native binding before the settling guard can delay its icon.
          if (this.mobileAdd.matches) new Set(records.map(record => record.target.closest('[data-photo-control]'))).forEach(card => { if (card) this.mobileAddIcons(card); });
          this.settle();
        });
        this.controlObserver.observe(this.controls, { subtree: true, attributes: true, attributeFilter: ['data-rudca-basket-card', 'disabled'] });
      }
      this.controls?.addEventListener('click', (event) => {
        if (event.target.closest('[data-photo-placeholder-add]')) this.queuePendingAdd(event);
      }, { signal });
      document.addEventListener('rudca:cart-settled', () => this.settle(), { signal });
      document.addEventListener('rudca:basket-state', () => this.controlCards.forEach(card => this.mobileAddIcons(card)), { signal });
      document.addEventListener('rudca:basket-intent', (event) => this.revealStock(event.detail?.button), { signal });
      this.scheduleSettle();
      this.rail.addEventListener('keydown', (event) => this.key(event), { signal });
      this.rail.addEventListener('focusin', (event) => {
        this.pointerScrollFocus = this.pointer ? document.activeElement : null;
        if (this.pointer || event.target.closest('[data-photo-control]')) return;
        const index = this.links.indexOf(event.target.closest('[data-photo-link]') || event.target.closest('[data-photo-item]')?.querySelector('[data-photo-link]'));
        if (index >= 0) this.go(index, false);
      }, { signal });
      this.rail.addEventListener('pointerdown', (event) => this.down(event), { passive: true, signal });
      this.rail.addEventListener('pointermove', (event) => this.move(event), { passive: false, signal });
      window.addEventListener('pointerup', (event) => this.up(event), { signal });
      window.addEventListener('pointercancel', (event) => this.up(event), { signal });
      this.rail.addEventListener('lostpointercapture', (event) => this.up(event), { signal });
      this.rail.addEventListener('click', (event) => {
        if (Date.now() < this.suppressUntil) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal });
      this.rail.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.motion.addEventListener('change', () => this.schedulePaint(), { signal });
      if (window.ResizeObserver) {
        this.resize = new ResizeObserver(() => {
          if (signal.aborted || !this.isConnected) return;
          const current = this.current;
          this.measure();
          this.go(current, false);
          this.paint();
        });
        this.resize.observe(this.rail);
      }
    }
    disconnectedCallback() {
      this.connection?.abort();
      this.connection = null;
      this.stop();
    }
    stop() {
      this.visibility?.abort();
      this.visibility = null;
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;
      this.cancelStartTask();
      this.photoWarmup = false;
      this.resolvePhotoDemand?.(); this.resolvePhotoDemand = null; this.photoDemand = null;
      this.cancelPendingAdd();
      this.releaseClick();
      this.intent = null;
      this.controlCards?.forEach((card) => { card.inert = true; });
      this.controller?.abort();
      this.pages?.stop();
      this.pages = null;
      this.controller = null;
      this.resize?.disconnect();
      this.resize = null;
      this.controlObserver?.disconnect();
      this.controlObserver = null;
      clearTimeout(this.settleTimer);
      clearTimeout(this.trackTimer); cancelAnimationFrame(this.trackFrame);
      this.touchActive = false; this.reserveActive = false; this.trackSettled = false;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.scales = null;
      this.pointer = null;
      this.pointerScrollFocus = null;
      this.rail?.removeAttribute('data-dragging');
    }
    releasePointerScrollFocus() {
      const focused = this.pointerScrollFocus;
      if (!focused) return;
      if (focused !== document.activeElement || !this.rail.contains(focused)) { this.pointerScrollFocus = null; return; }
      const item = focused.closest('[data-photo-item]');
      if (!item) return;
      const box = item.getBoundingClientRect(), rail = this.rail.getBoundingClientRect();
      // Native touch scrolling can leave a previously tapped Add button focused.
      // Once that exact product is offscreen, retire only this pointer focus so
      // it cannot pin an old page forever. Keyboard focus and live forms remain.
      if (box.right <= rail.left || box.left >= rail.right) {
        this.pointerScrollFocus = null;
        focused.blur();
      }
    }
    lock() {
      const updateCards = !this.locked || this.lockedCards !== this.controlCards;
      this.releaseClick();
      if (!this.locked) this.selectionStarted = performance.now();
      this.locked = true;
      this.trackSettled = false;
      this.lastMotion = performance.now();
      this.epoch++;
      this.intent = null;
      clearTimeout(this.settleTimer);
      if (this.controls) {
        if (!this.controls.hasAttribute('data-moving')) this.controls.dataset.moving = '';
        // A new mobile Add gesture must reach its exact button during settling.
        // The capture guard still blocks native actions until a valid release.
        if (updateCards) {
          this.lockedCards = this.controlCards;
          this.controlCards?.forEach((card) => { const inert = !this.coldControl(card) && !this.mobileAdd.matches; if (card.inert !== inert) card.inert = inert; });
        }
      }
    }
    cardVisible(card, railBox) {
      if (!card || !card.isConnected || card.hidden || !this.controls?.contains(card)) return false;
      const box = card.getBoundingClientRect(), rail = railBox || this.rail.getBoundingClientRect();
      return box.width > 0 && box.height > 0 && Math.min(box.right,rail.right) - Math.max(box.left,rail.left) >= 44;
    }
    validIntent(intent) {
      return Boolean(intent && intent.epoch === this.epoch && Math.abs(this.rail.scrollLeft - intent.left) <= 1.5 && this.cardVisible(intent.card) &&
        intent.card.closest('[data-photo-item]')?.dataset.photoItem === intent.card.dataset.photoControl &&
        intent.link === intent.card.closest('[data-photo-item]')?.querySelector('[data-photo-link]') &&
        this.samePendingImage(intent.image,intent.link) && intent.fingerprint === this.pendingFingerprint(intent.card));
    }
    releaseClick() {
      clearTimeout(this.clickTimer);
      if (!this.pointer?.released) return;
      this.pointer = null;
      this.scheduleSettle(); this.scheduleTrackCalm();
    }
    pendingControl() { this.scheduleSettle(); }
    hideStockHints() {
      this.controlCards?.forEach((card) => { if (card.hasAttribute('data-photo-stock-engaged')) card.removeAttribute('data-photo-stock-engaged'); });
    }
    revealStock(target) {
      const button = target instanceof Element ? target.closest('[data-rudca-grid-add], [data-rudca-basket-change]') : null;
      const card = button?.closest('[data-photo-control]');
      if (this.pointer || !this.cardVisible(card)) return;
      if (this.locked) {
        const stock = card.querySelector('[data-rudca-photo-stock][data-rudca-stock-synced]');
        const form = button.closest('form[data-rudca-basket-card]');
        if (!this.mobileAdd.matches || !button.disabled || !button.matches('[data-rudca-basket-change="1"]') ||
          this.intent?.button !== button || !this.validIntent(this.intent) || !form || !stock || stock.hidden || !stock.textContent.trim() ||
          stock.dataset.rudcaStockVariant !== form.querySelector('[name="id"]')?.value) return;
      }
      const expiredStatus = card.querySelector('[data-photo-cold-status][data-photo-pending-error]');
      expiredStatus?.remove();
      card.dataset.photoStockEngaged = '';
      if (expiredStatus) this.syncPendingStock();
    }
    coldControl(card) {
      const form = card?.querySelector('form');
      return Boolean(card?.hasAttribute('data-photo-control-loading') || card?.hasAttribute('data-photo-control-binding') || form && !form.hasAttribute('data-rudca-basket-card'));
    }
    mobileAddIcons(card) {
      const source = card.querySelector('[data-rudca-grid-add] svg.icon--cart');
      const form = card.querySelector('form[data-rudca-basket-card]');
      const group = form?.querySelector('.rudca-basket-card__quantity');
      const count = group?.querySelector('.rudca-basket-card__count');
      // The native group/count already resolve the exact purchase identity and
      // optimistic target, including zero, failures and newly bound copies.
      const added = this.mobileAdd.matches && group && !group.hidden && Number(count?.textContent) > 0;
      card.querySelectorAll('.rudca-basket-card__plus').forEach(button => {
        if (this.mobileAdd.matches && source && !button.querySelector('[data-photo-mobile-add-icon]')) {
          const icon = source.cloneNode(true);
          icon.setAttribute('data-photo-mobile-add-icon',''); icon.setAttribute('aria-hidden','true');
          button.appendChild(icon);
        }
        let description = button.querySelector('[data-photo-added-description]');
        if (added && !description) {
          const check = document.createElement('span');
          check.setAttribute('data-photo-added-check',''); check.setAttribute('aria-hidden','true'); check.textContent = '✓';
          description = document.createElement('span'); description.hidden = true;
          description.setAttribute('data-photo-added-description',''); description.id = 'rudca-photo-added-' + (++addedDescriptionId);
          button.append(check, description);
        }
        const check = button.querySelector('[data-photo-added-check]');
        const badge = added ? count.textContent.trim() + ' ✓' : '';
        if (check && check.textContent !== badge) check.textContent = badge;
        if (Boolean(added) !== button.hasAttribute('data-photo-added')) button.toggleAttribute('data-photo-added', Boolean(added));
        if (!description) return;
        const label = added ? count.getAttribute('aria-label') || group.getAttribute('aria-label') || '' : '';
        if (description.textContent !== label) description.textContent = label;
        const ids = (button.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== description.id);
        if (added) ids.push(description.id);
        const describedBy = ids.join(' ');
        if (describedBy !== (button.getAttribute('aria-describedby') || '')) {
          if (describedBy) button.setAttribute('aria-describedby', describedBy);
          else button.removeAttribute('aria-describedby');
        }
      });
    }
    pendingImage(link) {
      const image = link?.querySelector('img');
      if (!this.mobileAdd.matches) return image?.currentSrc || image?.src;
      // The browser may finish choosing/decoding this same declared image during a tap.
      // Keep the exact node and its declared sources fixed, rather than currentSrc.
      return image ? {node:image, src:image.getAttribute('src'), srcset:image.getAttribute('srcset'), photoSrc:image.getAttribute('data-photo-src')} : null;
    }
    samePendingImage(snapshot, link) {
      const image = link?.querySelector('img');
      if (!snapshot || typeof snapshot !== 'object') return snapshot === (image?.currentSrc || image?.src);
      return snapshot.node === image && snapshot.src === image.getAttribute('src') &&
        snapshot.srcset === image.getAttribute('srcset') && snapshot.photoSrc === image.getAttribute('data-photo-src');
    }
    pendingFingerprint(card) {
      const form = card?.querySelector('form');
      if (!form) return null;
      return [...form.querySelectorAll('[name="id"], [name="selling_plan"], [name^="properties["]')]
        .map((input) => [input.name,input.value]).sort((a,b) => a[0].localeCompare(b[0])).map((pair) => pair.join('=')).join('&');
    }
    cancelShiftedPendingAdds() {
      for (const [id,pending] of this.pendingAdds || []) {
        if (Math.abs(this.rail.scrollLeft - pending.left) > 1.5) this.cancelPendingAdd(id);
      }
    }
    syncPendingStock() {
      window.RudcaPhotoBasket?.syncStock?.();
    }
    cancelPendingAdd(id, syncStock = true) {
      if (!this.pendingAdds) return;
      for (const [key,pending] of this.pendingAdds) {
        if (id && key !== id) continue;
        clearTimeout(pending.timer); this.pendingAdds.delete(key);
        const card = this.controlCards?.find(card => card.dataset.photoControl === key);
        card?.removeAttribute('data-photo-add-pending');
        const status = card?.querySelector('[data-photo-cold-status]');
        if (status && card.hasAttribute('data-photo-control-loading')) status.textContent = this.dataset.photoSelectionLoading || '';
        else status?.remove();
      }
      if (syncStock) this.syncPendingStock();
    }
    queuePendingAdd(event, intent) {
      const card = intent?.card || event.target.closest('[data-photo-control]');
      if (this.pointer || !this.cardVisible(card) || (this.locked && !this.validIntent(intent)) ||
        (!this.coldControl(card) && !intent?.cold && !intent?.settling)) return false;
      card.querySelector('[data-photo-cold-status][data-photo-pending-error]')?.remove();
      const id = card.dataset.photoControl;
      const queued = this.pendingAdds.get(id);
      if (queued) {
        // Desktop retains one cold intent; each fresh mobile gesture is one Add.
        if (this.mobileAdd.matches && intent && event.type === 'pointerup' && event.pointerType === 'touch') {
          queued.count++;
          const status = card.querySelector('[data-photo-cold-status]');
          if (status) status.textContent = (this.dataset.photoPendingLabel || '').replace('__COUNT__',String(queued.count));
          this.syncPendingStock();
        }
        return true;
      }
      const link = card.closest('[data-photo-item]')?.querySelector('[data-photo-link]');
      const pending = {id, link, card:intent?.settling ? card : null, count:1, left:intent?.left ?? this.rail.scrollLeft, image:this.pendingImage(link), fingerprint:this.pendingFingerprint(card), expires:performance.now()+5000};
      this.pendingAdds.set(id,pending); card.dataset.photoAddPending = '';
      card.dataset.photoStockEngaged = '';
      pending.timer = setTimeout(() => {
        this.cancelPendingAdd(id);
        const current = this.controlCards.find(candidate => candidate.dataset.photoControl === id);
        let status = current?.querySelector('[data-photo-cold-status]');
        if (current && !status) { status = document.createElement('p'); status.dataset.photoColdStatus=''; status.setAttribute('role','status'); current.appendChild(status); }
        if (status) { status.textContent = this.querySelector('[data-photo-retry]')?.textContent.trim() || ''; status.setAttribute('data-photo-pending-error', ''); status.setAttribute('role', 'alert'); }
        this.syncPendingStock();
      },5000);
      let status = card.querySelector('[data-photo-cold-status]');
      if (!status) { status = document.createElement('p'); status.dataset.photoColdStatus=''; status.setAttribute('role','status'); card.appendChild(status); }
      status.textContent = (this.dataset.photoPendingLabel || '').replace('__COUNT__','1');
      this.syncPendingStock();
      this.pages?.controls();
      return true;
    }
    flushPendingAdd(card) {
      const pending = this.pendingAdds?.get(card.dataset.photoControl);
      if (!pending) return;
      if (this.locked || this.pointer || (pending.card && pending.card !== card) || Math.abs(this.rail.scrollLeft - pending.left) > 1.5 || !this.cardVisible(card) || pending.link !== card.closest('[data-photo-item]')?.querySelector('[data-photo-link]') || !this.samePendingImage(pending.image,pending.link) || performance.now()>pending.expires) { this.cancelPendingAdd(pending.id); return; }
      const form = card.querySelector('form[data-rudca-basket-card]');
      if (!form) {
        if (!card.querySelector('form')) this.cancelPendingAdd(pending.id);
        return;
      }
      if (pending.fingerprint != null && pending.fingerprint !== this.pendingFingerprint(card)) { this.cancelPendingAdd(pending.id); return; }
      const failed = form.querySelector('.rudca-basket-card__error');
      if (failed && !failed.hidden && failed.textContent.trim()) { this.cancelPendingAdd(pending.id); return; }
      const button = [...form.querySelectorAll('[data-rudca-grid-add], [data-rudca-basket-change="1"]')].find(node => !node.disabled && !node.hidden && !node.closest('[hidden]'));
      if (!button) {
        const rules = form.querySelector('[data-rudca-basket-selected]');
        const maximum = rules?.getAttribute('data-max');
        const minimum = Number(rules?.getAttribute('data-min') || 1);
        const quantity = Number(form.querySelector('.rudca-basket-card__count')?.textContent || 0);
        const error = form.querySelector('.rudca-basket-card__error');
        const choice = form.querySelector('.rudca-basket-card__link:not([hidden])');
        if (choice || (maximum != null && (Number(maximum) < minimum || quantity >= Number(maximum))) || (error && !error.hidden && error.textContent.trim())) {
          this.cancelPendingAdd(pending.id); card.dataset.photoStockEngaged = '';
        } else if (pending.fingerprint == null) pending.fingerprint = this.pendingFingerprint(card);
        return;
      }
      // Once a cold shell resolves, later increments keep this exact native form.
      pending.card = card;
      if (pending.fingerprint == null) pending.fingerprint = this.pendingFingerprint(card);
      pending.count--;
      // Transfer the last queued tap to the native ledger in one synchronous
      // turn, without briefly adding its stock back between the two queues.
      if (!pending.count) this.cancelPendingAdd(pending.id, false);
      else {
        const status = card.querySelector('[data-photo-cold-status]');
        if (status) status.textContent = (this.dataset.photoPendingLabel || '').replace('__COUNT__',String(pending.count));
        this.scheduleSettle();
      }
      // The native Add/plus remains the only writer and enforces stock/quantity rules.
      button.click();
      this.syncPendingStock();
    }
    centred() {
      const nearest = this.nearest();
      const max = Math.max(0,this.rail.scrollWidth - this.rail.clientWidth);
      const clamped = this.rail.scrollLeft <= 1.5 || this.rail.scrollLeft >= max - 1.5;
      return !this.pointer && (clamped || Math.abs(this.centres[nearest] - this.rail.scrollLeft - this.rail.clientWidth / 2) < 1.5);
    }
    boundaryIntent(direction) {
      const max = Math.max(0,this.rail.scrollWidth - this.rail.clientWidth);
      if (direction === -1 ? this.rail.scrollLeft <= 1.5 : this.rail.scrollLeft >= max - 1.5) {
        this.pages?.intent(direction);
        // A clamped wheel gesture produces no scroll event. Reconsider a page
        // whose focus or basket pin was released without requiring another drag.
        this.scheduleSettle();
      }
    }
    finishTrackMotion() {
      if (!this.controller || this.controller.signal.aborted || !this.isConnected || this.pointer || this.touchActive) return;
      this.reserveActive = false; this.trackSettled = true;
      this.pages?.trimReserve(); this.settle();
    }
    scheduleTrackCalm() {
      clearTimeout(this.trackTimer); cancelAnimationFrame(this.trackFrame);
      const signal = this.controller?.signal;
      if (!signal || signal.aborted || !this.pages) return;
      this.trackTimer = setTimeout(() => {
        if (signal.aborted || !this.isConnected || this.pointer || this.touchActive) return;
        const left = this.rail.scrollLeft, motion = this.lastMotion;
        this.trackFrame = requestAnimationFrame(() => {
          this.trackFrame = requestAnimationFrame(() => {
            if (signal.aborted || !this.isConnected || this.pointer || this.touchActive) return;
            if (motion !== this.lastMotion || left !== this.rail.scrollLeft || performance.now() - this.lastMotion < 180) {
              this.scheduleTrackCalm(); return;
            }
            this.finishTrackMotion();
          });
        });
      },Math.max(0,180 - (performance.now() - this.lastMotion)));
    }
    scheduleSettle() {
      if (!this.controller || this.controller.signal.aborted) return;
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 40);
    }
    settle() {
      if (!this.controller || this.controller.signal.aborted || !this.isConnected || !this.controls || this.pointer) return;
      if (performance.now() - this.lastMotion < 40) { this.scheduleSettle(); return; }
      if (this.pages?.commit()) return;
      this.pages?.controls();
      this.bound = this.nearest();
      const id = this.links[this.bound]?.dataset.photoProduct;
      this.locked = false;
      this.controls.removeAttribute('data-moving');
      this.controls.removeAttribute('aria-busy');
      // Reveal first, then read geometry together. Inert/aria writes must not
      // force a new layout before the next card's visibility read.
      this.controlCards.forEach(card => { if (card.hidden) card.hidden = false; });
      const railBox = this.rail.getBoundingClientRect();
      const states = this.controlCards.map(card => {
        const link = card.closest('[data-photo-item]')?.querySelector('[data-photo-link]');
        const form = card.querySelector('form');
        const loading = card.hasAttribute('data-photo-control-loading');
        // A bound mobile form is purchase-ready independently of decorative image decoding.
        // The native basket engine still owns disabled, identity and stock checks.
        const ready = !loading && (!form || form.hasAttribute('data-rudca-basket-card')) &&
          (this.mobileAdd.matches && form?.hasAttribute('data-rudca-basket-card') || this.pages?.imageReady(link));
        const inert = !this.cardVisible(card,railBox) || (!ready && !this.coldControl(card) && !this.mobileAdd.matches);
        return {card,ready,inert};
      });
      states.forEach(({card,ready,inert}) => {
        this.mobileAddIcons(card);
        if (card.inert !== inert) card.inert = inert;
        if (ready) {
          if (card.hasAttribute('data-photo-control-binding')) card.removeAttribute('data-photo-control-binding');
          if (card.hasAttribute('aria-busy')) card.removeAttribute('aria-busy');
        }
        else if (card.getAttribute('aria-busy') !== 'true') card.setAttribute('aria-busy','true');
      });
      states.forEach(({card,ready,inert}) => { if (!inert && ready) this.flushPendingAdd(card); });
      if (this.dataset.photoSelectedId !== id || this.selectionStarted != null) {
        this.dataset.photoSelectedId = id;
        this.dataset.photoSelectionMs = String(Math.round(performance.now() - (this.selectionStarted ?? performance.now())));
        this.dataset.photoSettleMs = String(Math.round(performance.now() - this.lastMotion));
        this.selectionStarted = null;
      }
    }
    measure() {
      this.centres = this.links.map((link) => link.offsetLeft + link.offsetWidth / 2);
      this.step = this.links[0].offsetWidth || 72;
    }
    nearest(centre = this.rail.scrollLeft + this.rail.clientWidth / 2) {
      return this.centres.reduce((best, value, index) => Math.abs(value - centre) < Math.abs(this.centres[best] - centre) ? index : best, 0);
    }
    schedulePaint() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.paint(); });
    }
    paint() {
      // position() may synchronously rebase the window and paint it. Take the
      // geometry snapshot afterward so the outer paint cannot restore old scales.
      this.pages?.position();
      const centre = this.rail.scrollLeft + this.rail.clientWidth / 2;
      this.current = this.nearest(centre);
      const scales = this.scales || (this.scales = new WeakMap());
      this.links.forEach((link, index) => {
        const distance = Math.min(1, Math.abs(this.centres[index] - centre) / (this.step * 2));
        const focus = Math.max(0, 1 - Math.abs(this.centres[index] - centre) / this.step);
        const scale = 1 - .34 * distance + .3225 * focus;
        const value = (this.motion.matches ? (index === this.current ? 1.3225 : 1) : scale).toFixed(4);
        if (scales.get(link) !== value) { link.style.setProperty('--photo-scale', value); scales.set(link,value); }
      });
    }
    go(index, animate) {
      if (this.links?.[index]?.dataset.photoProduct !== this.links?.[this.current]?.dataset.photoProduct) this.hideStockHints();
      this.cancelPendingAdd();
      this.lock();
      this.current = Math.max(0, Math.min(index, this.links.length - 1));
      this.rail.scrollTo({ left: this.centres[this.current] - this.rail.clientWidth / 2, behavior: animate && !this.motion.matches ? 'smooth' : 'auto' });
      this.scheduleSettle();
    }
    key(event) {
      this.reserveActive = true; this.trackSettled = false;
      this.scheduleTrackCalm();
      this.pointerScrollFocus = null;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target.closest('[data-photo-control]')) return;
      const index = this.links.indexOf(event.target.closest('[data-photo-link]') || event.target.closest('[data-photo-item]')?.querySelector('[data-photo-link]'));
      if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let next = index;
      if (event.key === 'ArrowLeft') next--;
      if (event.key === 'ArrowRight') next++;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.links.length - 1;
      if (next < 0 || next >= this.links.length) {
        const from = this.links[index], direction = next < 0 ? -1 : 1;
        this.pages?.intent(direction);
        next = this.links.indexOf(from) + direction;
      }
      next = Math.max(0, Math.min(next, this.links.length - 1));
      const origin = event.target.closest('a.rudca-photo-scroll__origin') && this.links[next].closest('[data-photo-item]')?.querySelector('a.rudca-photo-scroll__origin');
      (origin || this.links[next]).focus({ preventScroll: true });
      this.go(next, false);
    }
    down(event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.suppressUntil = 0;
      this.reserveActive = true; this.trackSettled = false;
      this.pointerScrollFocus = this.rail.contains(document.activeElement) ? document.activeElement : null;
      const controlPress = Boolean(event.target.closest('[data-photo-control]'));
      if (!controlPress) { this.hideStockHints(); this.cancelPendingAdd(); this.lock(); }
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.rail.scrollLeft, controlPress, mouse: event.pointerType === 'mouse', touch: event.pointerType === 'touch', moved: false, dragging: false };
    }
    move(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (Math.hypot(dx, dy) > 8) {
        pointer.moved = true;
        if (pointer.controlPress && !this.locked) { this.cancelPendingAdd(); this.hideStockHints(); this.lock(); }
      }
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) this.boundaryIntent(dx > 0 ? -1 : 1);
      if (!pointer.mouse) return; // Touch retains native momentum and scroll snapping.
      if (!pointer.dragging && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
        pointer.dragging = true;
        this.rail.dataset.dragging = '';
        this.rail.setPointerCapture(event.pointerId);
      }
      if (pointer.dragging) {
        event.preventDefault();
        this.rail.scrollLeft = pointer.left - dx;
      }
    }
    up(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      if (pointer.released && event.type !== 'pointercancel') return;
      if (event.type === 'pointerup' && Math.hypot(event.clientX - pointer.x,event.clientY - pointer.y) > 8) pointer.moved = true;
      const cancelled = event.type !== 'pointerup';
      this.pointer = null;
      if (!pointer.moved && !cancelled) this.pointerScrollFocus = null;
      if (pointer.moved || cancelled) { this.intent = null; this.suppressUntil = Date.now() + 400; }
      if (this.rail.hasPointerCapture(event.pointerId)) this.rail.releasePointerCapture(event.pointerId);
      this.rail.removeAttribute('data-dragging');
      if (pointer.dragging) this.go(this.nearest(), true);
      else this.scheduleSettle();
      this.scheduleTrackCalm();
      if (pointer.controlPress && !pointer.moved && !cancelled && (this.intent?.cold || this.intent?.settling) && this.validIntent(this.intent) &&
        this.intent.button?.isConnected && this.intent.card.contains(this.intent.button) && this.queuePendingAdd(event,this.intent)) {
        // Cold/mobile settling taps complete at release, even if no compatibility
        // click survives the transition. The captured echo is consumed.
        this.intent.consumed = true;
        return;
      }
      if (this.mobileAdd.matches && pointer.touch && pointer.controlPress && !pointer.moved && !cancelled && !this.locked && this.validIntent(this.intent)) {
        const button = this.intent.button;
        if (button?.isConnected && this.intent.card.contains(button) && !button.disabled && !button.closest('[hidden]') &&
          !this.coldControl(this.intent.card) && button.closest('form[data-rudca-basket-card]') &&
          button.matches('[data-rudca-grid-add], [data-rudca-basket-change="1"]')) {
          // A fresh stationary mobile release is the Add gesture. Safari may omit
          // its compatibility click after a fling; consume any echo exactly once.
          this.intent.consumed = true;
          button.click();
          return;
        }
      }
      if (pointer.touch && pointer.controlPress && !pointer.moved && !cancelled && !this.locked && this.validIntent(this.intent)) {
        // Safari can deliver its native click after pointerup and a render task.
        // Keep the exact control pinned until that click or bounded cancellation.
        pointer.released = true; this.pointer = pointer;
        this.clickTimer = setTimeout(() => {
          if (this.pointer !== pointer) return;
          this.intent = null; this.releaseClick();
        },750);
      }
    }
  }
  customElements.define('rudca-photo-scroll', RudcaPhotoScroll);
})();
